import { Injectable, signal } from '@angular/core';

/** Which sidebar notification dots the dev/QA override forces on. */
export type NotifDotTestState =
  /** No override — the dots follow the real missing-routing counts. */
  | 'none'
  /** Force both the Approvisionnements and Expéditions dots on. */
  | 'both'
  /** Force only the Approvisionnements dot on. */
  | 'approvisionnements'
  /** Force only the Expéditions dot on. */
  | 'expeditions'
  /** Force both dots off, regardless of the real counts. */
  | 'off';

export type NotifDotSection = 'approvisionnements' | 'expeditions';

/** The cycle order the `~notifdots` dev command steps through. */
const CYCLE: NotifDotTestState[] = [
  'none',
  'both',
  'approvisionnements',
  'expeditions',
  'off',
];

/**
 * Dev/QA-only override for the sidebar's "missing routing" notification dots,
 * so an internal user can preview each dot state from the `~` command menu
 * without needing real receptions/expeditions with missing routing. Not a real
 * feature and not customer-facing — the palette's `~notifdots` command cycles
 * it and the sidebar reads it, falling back to the real counts when `'none'`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDotTestService {
  readonly state = signal<NotifDotTestState>('none');

  /** Advance to the next test state and return it (for the dev command's toast). */
  cycle(): NotifDotTestState {
    const next = CYCLE[(CYCLE.indexOf(this.state()) + 1) % CYCLE.length];
    this.state.set(next);
    return next;
  }

  /**
   * The forced dot visibility for a section, or `null` when there is no override
   * in effect (`'none'`) so the caller should use the real count.
   */
  override(section: NotifDotSection): boolean | null {
    const state = this.state();
    if (state === 'none') return null;
    if (state === 'off') return false;
    if (state === 'both') return true;
    return state === section;
  }
}
